import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play } from "lucide-react";
import { GALLERY } from "@/lib/data";
import { fetchMedia, FILE_URL } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const SPANS = [
  "md:col-span-7 md:row-span-2",
  "md:col-span-5",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-7",
  "md:col-span-5",
];

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    fetchMedia("gallery")
      .then((data) =>
        setUploaded(
          data.map((m, i) => ({
            id: m.id,
            type: m.media_type,
            src: FILE_URL(m.url),
            alt: m.caption || "Mask Giver outreach",
            tag: m.media_type === "video" ? "Video" : "Uploaded",
            span: SPANS[i % SPANS.length],
          }))
        )
      )
      .catch(() => {});
  }, []);

  const placeholders = GALLERY.map((g) => ({ ...g, type: "image" }));
  const items = [...uploaded, ...placeholders];

  return (
    <section id="gallery" data-testid="gallery" className="relative bg-[#0a0a0a] py-24 md:py-36 border-y border-[#1a1a1a]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <p className="label mb-5">On The Ground</p>
            <h2 className="font-display uppercase text-white leading-[0.95] text-4xl sm:text-6xl lg:text-7xl">
              The <span className="text-green glow-green">work</span>, unfiltered
            </h2>
          </div>
          <p className="font-sub text-zinc-400 max-w-sm font-light">
            Real moments from our team feeding the homeless and giving out clothes, shoes,
            water and juice.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] md:auto-rows-[230px] gap-4">
          {items.map((g, i) => (
            <motion.button
              key={g.id || i}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(g)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
              className={`group relative overflow-hidden bg-[#111] ${g.span} text-left`}
            >
              {g.type === "video" ? (
                <>
                  <video src={g.src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="bg-green text-black rounded-full p-4 group-hover:scale-110 transition-transform"><Play size={22} /></span>
                  </span>
                </>
              ) : (
                <img src={g.src} alt={g.alt} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
              )}
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/45 transition-colors duration-500 pointer-events-none" />
              <span className="absolute top-4 left-4 label !text-white/80 bg-black/40 backdrop-blur px-3 py-1 rounded-full">
                {g.tag}
              </span>
            </motion.button>
          ))}
        </div>

        <p className="font-sub text-sm text-zinc-500 mt-8 max-w-xl">
          Placeholder photos are shown until you add your own. Upload real photos & videos
          from the <a href="/admin" className="text-green link-underline">admin studio</a> — they appear here instantly.
        </p>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md flex items-center justify-center p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-green"
              onClick={() => setActive(null)}
              data-testid="lightbox-close"
            >
              <X size={30} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {active.type === "video" ? (
                <video src={active.src} className="w-full max-h-[78vh]" controls autoPlay playsInline />
              ) : (
                <img src={active.src} alt={active.alt} className="w-full max-h-[78vh] object-contain" />
              )}
              <figcaption className="font-sub text-zinc-400 mt-4 text-center">{active.alt}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
